import { getAuthHeaders } from '../../../services/authService';

const API_BASE_URL = '/api/analysis';

const checkResponseType = (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    throw new Error('Backend API not available. Please ensure the backend server is running or deploy to Vercel.');
  }
};

export const PORTFOLIO_ASSETS = [
  {
    id: 'gold',
    name: 'Gold',
    symbol: 'GC=F',
    category: 'Commodity',
    description: 'COMEX gold futures (USD per troy ounce)',
    color: '#d97706'
  },
  {
    id: 'stocks',
    name: 'US Stocks (S&P 500)',
    symbol: '^GSPC',
    category: 'Equity Index',
    description: 'S&P 500 index (USD)',
    color: '#2563eb'
  },
  {
    id: 'bitcoin',
    name: 'Bitcoin',
    symbol: 'BTC-USD',
    category: 'Cryptocurrency',
    description: 'Bitcoin spot price (USD)',
    color: '#9333ea'
  }
];

export const getPortfolioAsset = (assetId) =>
  PORTFOLIO_ASSETS.find((asset) => asset.id === assetId);

const buildYahooUrl = (symbol, range = '3y', interval = '1d') => {
  const params = new URLSearchParams({
    symbol,
    interval,
    range
  });

  return `${API_BASE_URL}/yahoo/chart?${params.toString()}`;
};

const normalizeYahooSeries = (yahooResult) => {
  const timestamps = yahooResult.timestamp || [];
  const closes = yahooResult.indicators?.quote?.[0]?.close || [];

  return timestamps
    .map((timestamp, index) => {
      const close = closes[index];
      if (close === null || close === undefined || Number.isNaN(close)) {
        return null;
      }

      const epochMs = timestamp * 1000;

      return {
        date: new Date(epochMs).toISOString(),
        timestamp: epochMs,
        close
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.timestamp - b.timestamp);
};

export const fetchAssetHistory = async (assetId, { range = '3y', interval = '1d' } = {}) => {
  const asset = getPortfolioAsset(assetId);

  if (!asset) {
    throw new Error(`Unknown portfolio asset: ${assetId}`);
  }

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(buildYahooUrl(asset.symbol, range, interval), { headers });

    checkResponseType(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to load ${asset.name} history (status ${response.status})`);
    }

    const data = await response.json();

    if (!data?.chart?.result?.length) {
      throw new Error(`No history returned for ${asset.name}`);
    }

    const result = data.chart.result[0];
    const series = normalizeYahooSeries(result);

    if (series.length === 0) {
      throw new Error(`Empty history for ${asset.name}`);
    }

    return {
      asset,
      meta: result.meta || {},
      series
    };
  } catch (error) {
    console.error(`Portfolio asset history error for ${asset?.name || assetId}:`, error);
    throw error;
  }
};

export const fetchAllPortfolioAssets = async (options = {}) => {
  const results = await Promise.all(
    PORTFOLIO_ASSETS.map(async (asset) => {
      try {
        return await fetchAssetHistory(asset.id, options);
      } catch (error) {
        return { asset, error };
      }
    })
  );

  return results;
};
// src/products/analysis/services/portfolioService.js
// Using localStorage instead of database for now

const STORAGE_KEY = 'analysis_portfolio_stocks';

/**
 * Get portfolio from localStorage
 */
const getPortfolioFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading portfolio from storage:', error);
    return [];
  }
};

/**
 * Save portfolio to localStorage
 */
const savePortfolioToStorage = (portfolio) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(portfolio));
  } catch (error) {
    console.error('Error saving portfolio to storage:', error);
    throw error;
  }
};

/**
 * Save a stock to user's portfolio
 */
export const saveStockToPortfolio = async (userId, stockData) => {
  try {
    const portfolio = getPortfolioFromStorage();
    
    // Check if already exists
    const existingIndex = portfolio.findIndex(
      stock => stock.symbol === stockData.symbol.toUpperCase()
    );

    const stockRecord = {
      id: existingIndex >= 0 ? portfolio[existingIndex].id : Date.now().toString(),
      symbol: stockData.symbol.toUpperCase(),
      name: stockData.name,
      exchange: stockData.exchange,
      currency: stockData.currency,
      shares_outstanding: stockData._sharesOutstanding,
      market_cap: stockData._marketCap,
      enterprise_value: stockData._enterpriseValue,
      current_price: stockData._currentPrice,
      shares_outstanding_formatted: stockData.sharesOutstanding,
      market_cap_formatted: stockData.marketCap,
      enterprise_value_formatted: stockData.enterpriseValue,
      current_price_formatted: stockData.currentPrice,
      logo: stockData.logo,
      weburl: stockData.weburl,
      saved_at: existingIndex >= 0 ? portfolio[existingIndex].saved_at : new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    if (existingIndex >= 0) {
      portfolio[existingIndex] = stockRecord;
    } else {
      portfolio.push(stockRecord);
    }

    savePortfolioToStorage(portfolio);
    return stockRecord;
  } catch (error) {
    console.error('Error saving stock to portfolio:', error);
    throw error;
  }
};

/**
 * Get user's saved stocks
 */
export const getUserPortfolio = async (userId) => {
  try {
    const portfolio = getPortfolioFromStorage();
    return portfolio.sort((a, b) => new Date(b.saved_at) - new Date(a.saved_at));
  } catch (error) {
    console.error('Error fetching portfolio:', error);
    return [];
  }
};

/**
 * Remove stock from portfolio
 */
export const removeStockFromPortfolio = async (stockId) => {
  try {
    const portfolio = getPortfolioFromStorage();
    const filtered = portfolio.filter(stock => stock.id !== stockId.toString());
    savePortfolioToStorage(filtered);
    return true;
  } catch (error) {
    console.error('Error removing stock from portfolio:', error);
    throw error;
  }
};

/**
 * Check if stock is already saved
 */
export const isStockSaved = async (userId, symbol) => {
  try {
    const portfolio = getPortfolioFromStorage();
    return portfolio.some(stock => stock.symbol === symbol.toUpperCase());
  } catch (error) {
    console.error('Error checking if stock is saved:', error);
    return false;
  }
};

/**
 * Update stock data in portfolio (refresh with latest data)
 */
export const updatePortfolioStock = async (stockId, stockData) => {
  try {
    const portfolio = getPortfolioFromStorage();
    const index = portfolio.findIndex(stock => stock.id === stockId.toString());
    
    if (index === -1) {
      throw new Error('Stock not found in portfolio');
    }

    portfolio[index] = {
      ...portfolio[index],
      shares_outstanding: stockData._sharesOutstanding,
      market_cap: stockData._marketCap,
      enterprise_value: stockData._enterpriseValue,
      current_price: stockData._currentPrice,
      shares_outstanding_formatted: stockData.sharesOutstanding,
      market_cap_formatted: stockData.marketCap,
      enterprise_value_formatted: stockData.enterpriseValue,
      current_price_formatted: stockData.currentPrice,
      updated_at: new Date().toISOString()
    };

    savePortfolioToStorage(portfolio);
    return portfolio[index];
  } catch (error) {
    console.error('Error updating portfolio stock:', error);
    throw error;
  }
};

